/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState, useEffect, useCallback } from 'react'
import { DARK_THEME, LIGHT_THEME } from '@vacationtracker/shared/constants/ThemeSetting'

export type ThemeMode = 'light' | 'dark' | 'system'

interface IThemeSetting {
  theme: string // The computed theme: 'light' or 'dark'
  themeMode: ThemeMode // The user's explicit choice: 'light', 'dark', or 'system'
  setThemeHandler: (theme: string) => void // Legacy: sets explicit light/dark
  setThemeMode: (mode: ThemeMode) => void // New: sets the theme mode including 'system'
}

interface IThemeProvider {
  children: React.ReactNode
}

const themeSetting: IThemeSetting = {
  theme: 'light',
  themeMode: 'system',
  // Placeholder functions
  setThemeHandler: () => {},
  setThemeMode: () => {},
}

export const ThemeContext = createContext(themeSetting)

const THEME_MODE_STORAGE_KEY = 'themeMode'
// Keep legacy key for backwards compatibility
const LEGACY_THEME_STORAGE_KEY = 'theme'

// This context provider is passed to any component requiring the context
export const ThemeProvider = ({ children }: IThemeProvider) => {
  const [theme, setTheme] = useState('light')
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system')

  const setThemeAndToggleClass = useCallback((newTheme: string) => {
    setTheme(newTheme)
    if (newTheme === DARK_THEME) {
      document.body.classList.add('dark-theme')
    } else {
      document.body.classList.remove('dark-theme')
    }
  }, [])

  const applySystemTheme = useCallback(
    (mql: MediaQueryList | MediaQueryListEvent) => {
      if (mql.matches) {
        setThemeAndToggleClass(DARK_THEME)
      } else {
        setThemeAndToggleClass(LIGHT_THEME)
      }
    },
    [setThemeAndToggleClass]
  )

  // Initialize theme on mount
  useEffect(() => {
    const darkMql = window.matchMedia('(prefers-color-scheme: dark)')

    // Check for new themeMode storage key first, then fall back to legacy
    const storedThemeMode = localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null
    const legacyTheme = localStorage.getItem(LEGACY_THEME_STORAGE_KEY)

    let initialMode: ThemeMode = 'system'

    if (storedThemeMode) {
      // New storage format
      initialMode = storedThemeMode
    } else if (legacyTheme) {
      // Migrate from legacy storage - if they had explicitly set a theme, use that
      initialMode = legacyTheme === DARK_THEME ? 'dark' : 'light'
      // Save in new format
      localStorage.setItem(THEME_MODE_STORAGE_KEY, initialMode)
    }

    setThemeModeState(initialMode)

    // Apply the initial theme
    if (initialMode === 'system') {
      applySystemTheme(darkMql)
    } else {
      setThemeAndToggleClass(initialMode === 'dark' ? DARK_THEME : LIGHT_THEME)
    }
  }, [applySystemTheme, setThemeAndToggleClass])

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    const darkMql = window.matchMedia('(prefers-color-scheme: dark)')

    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      if (themeMode === 'system') {
        applySystemTheme(event)
      }
    }

    // Use addEventListener/removeEventListener (addListener is deprecated)
    darkMql.addEventListener('change', handleSystemThemeChange)

    return () => {
      darkMql.removeEventListener('change', handleSystemThemeChange)
    }
  }, [themeMode, applySystemTheme])

  // Legacy handler for backwards compatibility
  const setThemeHandler = useCallback(
    (newTheme: string) => {
      const mode: ThemeMode = newTheme === DARK_THEME ? 'dark' : 'light'
      setThemeModeState(mode)
      setThemeAndToggleClass(newTheme)
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
    },
    [setThemeAndToggleClass]
  )

  // New handler that supports 'system' mode
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      setThemeModeState(mode)
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)

      if (mode === 'system') {
        const darkMql = window.matchMedia('(prefers-color-scheme: dark)')
        applySystemTheme(darkMql)
      } else {
        setThemeAndToggleClass(mode === 'dark' ? DARK_THEME : LIGHT_THEME)
      }
    },
    [applySystemTheme, setThemeAndToggleClass]
  )

  const value = {
    theme,
    themeMode,
    setThemeHandler,
    setThemeMode,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
